import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TrackingCodeInjector } from "@/components/TrackingCodeInjector";
import { AdminShortcutProvider } from "@/components/AdminShortcutProvider";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const BlogIndexPage = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPostPage = lazy(() => import("./pages/blog/BlogPost"));
const BlogAdminList = lazy(() => import("./pages/admin/blog/BlogAdminList"));
const BlogEditor = lazy(() => import("./pages/admin/blog/BlogEditor"));
const SialAdmin = lazy(() => import("./pages/admin/sial/SialAdmin"));
const ArticlesAdmin = lazy(() => import("./pages/admin/articles/ArticlesAdmin"));
const SialGuangzhou2026Page = lazy(() => import("./pages/insights/SialGuangzhou2026"));
const Admin = lazy(() => import("./pages/Admin"));
const ProjectEditor = lazy(() => import("./pages/ProjectEditor"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md px-4">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <TrackingCodeInjector />
          <BrowserRouter>
            <ScrollToTop />
            <AdminShortcutProvider>
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/portfolio" element={<Portfolio />} />
                    <Route path="/project/:slug" element={<ProjectDetail />} />
                    <Route path="/blog" element={<BlogIndexPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/guangzhou-2026" element={<SialGuangzhou2026Page />} />
                    {/* ลิงก์เดิมเปลี่ยนเส้นทางไปยัง URL ใหม่ที่สั้นกว่า */}
                    <Route path="/insights/sial-guangzhou-2026" element={<Navigate to="/guangzhou-2026" replace />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/project/:id" element={<ProjectEditor />} />
                    <Route path="/admin/blog" element={<BlogAdminList />} />
                    <Route path="/admin/blog/new" element={<BlogEditor />} />
                    <Route path="/admin/blog/:id" element={<BlogEditor />} />
                    <Route path="/admin/sial" element={<SialAdmin />} />
                    <Route path="/admin/articles" element={<ArticlesAdmin />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </AdminShortcutProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
